import { EnvelopeSimple } from '@phosphor-icons/react';
import { Button, Checkbox, Heading, SegmentedControl, Sheet, Surface, Text } from '@shh/ui';
import { useEffect, useState } from 'react';
import {
  adminInvite,
  adminOverview,
  adminWaitlist,
  friendlyError,
  type AdminOverview,
  type WaitlistEntry,
} from '../data/api';
import { useSession } from '../data/session';
import { adminText as t } from '../i18n/admin';
import { NotFoundPage } from '../app/NotFoundPage';
import { AdminAi } from './AdminAi';
import { AdminAccounts, AdminFamilies, AdminFunnel, AdminNumbers } from './AdminOverview';

const day = (ms: number) =>
  new Date(ms).toLocaleDateString('de-DE', { timeZone: 'Europe/Berlin', dateStyle: 'medium' });

/** The admin area in tabs (Mike, 26.10.) – the tab lives in the address (#warteliste …). */
const TABS = ['uebersicht', 'warteliste', 'familien', 'konten', 'ki'] as const;
type Tab = (typeof TABS)[number];
const tabFromHash = (): Tab => {
  const h = typeof window === 'undefined' ? '' : window.location.hash.slice(1);
  return (TABS as readonly string[]).includes(h) ? (h as Tab) : 'uebersicht';
};

type State =
  | { step: 'loading' }
  | { step: 'not-found' }
  | { step: 'ready'; unconfirmed: number; entries: WaitlistEntry[] };

/**
 * Testfamilien (1.17): only Mike (functions/admins.json) – pick families from the confirmed
 * waitlist and invite them with a personal link. No menu entry: app.larovihome.com/admin.
 * Since 1.18 also numbers, families and accounts (read only, counts and days).
 */
export function AdminPage() {
  const session = useSession();
  const [state, setState] = useState<State>({ step: 'loading' });
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string>();
  const [error, setError] = useState<string>();
  const [overview, setOverview] = useState<AdminOverview>();
  const [tab, setTab] = useState<Tab>(tabFromHash);
  const pick = (next: Tab) => {
    setTab(next);
    // replaceState, not the router: switching a tab must not scroll or add history entries
    window.history.replaceState(null, '', `#${next}`);
  };
  const signedIn = session.status === 'ready' || session.status === 'no-household';

  const load = () =>
    Promise.all([
      adminWaitlist().then(
        (r) => setState({ step: 'ready', ...r }),
        () => setState({ step: 'not-found' }),
      ),
      adminOverview().then(setOverview, () => undefined),
    ]);
  useEffect(() => {
    if (signedIn) void load();
  }, [signedIn]);

  if (session.status === 'loading') return null;
  if (!signedIn || state.step === 'not-found') return <NotFoundPage />;

  const send = async () => {
    setBusy(true);
    setError(undefined);
    try {
      const r = await adminInvite([...picked]);
      setDone(t.sent(r.invited.length));
      setPicked(new Set());
      setConfirm(false);
      await load();
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="mx-auto flex min-h-dvh max-w-default flex-col gap-section px-safe py-section">
      <Heading level={1} size="xl">
        {t.title}
      </Heading>
      <Text tone="secondary">{t.privacy}</Text>
      <SegmentedControl
        label={t.tabsLabel}
        value={tab}
        onChange={pick}
        options={TABS.map((v) => ({ value: v, label: t.tabs[v] }))}
        className="self-start max-sm:self-stretch"
      />
      {tab === 'uebersicht' &&
        (overview ? (
          <>
            <AdminNumbers data={overview} />
            <AdminFunnel data={overview} />
          </>
        ) : (
          <p role="status" className="text-text-secondary">
            {t.loadingOverview}
          </p>
        ))}
      {tab === 'warteliste' && (
        <Heading level={2} size="lg">
          {t.waitlistTitle}
        </Heading>
      )}
      {tab !== 'warteliste' ? null : state.step === 'loading' ? (
        <p role="status" className="text-text-secondary">
          {t.loading}
        </p>
      ) : (
        <>
          <Text tone="secondary">
            {t.counts(
              state.entries.length,
              state.entries.filter((e) => e.invitedAt).length,
              state.entries.filter((e) => e.joinedAt).length,
              state.unconfirmed,
            )}
          </Text>
          {done && (
            <p
              role="status"
              className="rounded-control bg-feedback-success-surface p-inset-sm font-semibold"
            >
              {done}
            </p>
          )}
          {state.entries.length === 0 ? (
            <Text>{t.empty}</Text>
          ) : (
            <Surface className="flex flex-col gap-gap-xs">
              <ul className="flex flex-col divide-y divide-border-subtle">
                {state.entries.map((e) => (
                  <li key={e.id} className="flex flex-wrap items-center gap-gap-md py-gap-sm">
                    <Checkbox
                      isSelected={picked.has(e.id)}
                      onChange={(on) => {
                        const next = new Set(picked);
                        if (on) next.add(e.id);
                        else next.delete(e.id);
                        setPicked(next);
                      }}
                      aria-label={t.select(e.email)}
                    >
                      <span className="font-semibold break-all">{e.email}</span>
                    </Checkbox>
                    <span className="text-sm text-text-secondary">
                      {t.since} {day(e.confirmedAt)}
                      {e.source ? ` · ${e.source}` : ''}
                    </span>
                    <span className="ml-auto text-sm font-semibold">
                      {e.joinedAt
                        ? t.joinedOn(day(e.joinedAt))
                        : e.invitedAt
                          ? t.invitedOn(day(e.invitedAt))
                          : t.open}
                    </span>
                  </li>
                ))}
              </ul>
            </Surface>
          )}
          <Button
            icon={EnvelopeSimple}
            className="self-start"
            isDisabled={picked.size === 0}
            onPress={() => {
              setDone(undefined);
              setConfirm(true);
            }}
          >
            {t.invite(picked.size)}
          </Button>
        </>
      )}
      {tab === 'ki' && <AdminAi />}
      {(tab === 'familien' || tab === 'konten') &&
        (overview ? (
          tab === 'familien' ? (
            <AdminFamilies data={overview} onChanged={() => void load()} />
          ) : (
            <AdminAccounts data={overview} />
          )
        ) : (
          <p role="status" className="text-text-secondary">
            {t.loadingOverview}
          </p>
        ))}
      <Sheet isOpen={confirm} onOpenChange={(o) => !busy && setConfirm(o)} title={t.confirmTitle}>
        <div className="flex flex-col gap-gap-md">
          <Text>{t.confirmText(picked.size)}</Text>
          {error && (
            <p role="alert" className="font-semibold text-feedback-danger">
              {error}
            </p>
          )}
          <div className="flex flex-wrap gap-gap-sm">
            <Button icon={EnvelopeSimple} isDisabled={busy} onPress={() => void send()}>
              {t.send}
            </Button>
            <Button variant="quiet" isDisabled={busy} onPress={() => setConfirm(false)}>
              {t.cancel}
            </Button>
          </div>
        </div>
      </Sheet>
    </main>
  );
}
